import { useSensors, useSensor, TouchSensor, MouseSensor, KeyboardSensor, DndContext, closestCenter, defaultDropAnimationSideEffects, defaultDropAnimation } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { cx } from '@linaria/core';
import { useRef, useState, useMemo, Fragment } from 'react';
import { CircularButton } from '@ui/kit';
import { ChevronIcon } from '@ui/kit/icons';
import { sequenceTileInput } from '@utils/component-framework';
import { useHasOverflow, CSSRefTransition, NoSSR } from '@utils/react';
import { useComponentRootRef } from '../../../../../context.js';
import { useOpgavekompleksetEntry } from '../../../../../state/hook.entry.js';
import { useUpdateTaskEntryMutation } from '../../../../../state/mutations/update-task-entry.js';
import { TaskAutoScroller } from '../../../../../utils/dnd-kit.auto-scroll.js';
import { makeRestrictToRef } from '../../../../../utils/dnd-kit.restrict-to-container.js';
import { OpgavekompleksetTaskDragOverlay } from '../../../../atom.drag-overlay/_drag-overlay.js';
import { useMatchTaskBreakpoint, taskBreakpoints } from '../../../hook.task-breakpoints.js';
import { SequenceInput } from '../input.content.js';
import { DraggableInput } from '../input.draggable.js';
import { orderInputs } from '../util.order-inputs.js';
import { updateInputsOnDrag } from '../util.update-inputs-on-drag-over.js';
import { wrapper, buttonFadeDurationMs, scrollButton, container, chevron } from './container.input.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';

function HorisontalInputContainer(props) {
  const ref = useRef(null);
  const componentRef = useComponentRootRef();
  const isMobile = useMatchTaskBreakpoint(taskBreakpoints.mobile);
  const [draggedInputId, setDraggedInputId] = useState();
  const update = useUpdateTaskEntryMutation();
  const [hasOverflow, {
    canScrollLeft,
    canScrollRight
  }] = useHasOverflow(ref);
  const handleScrollLeft = () => {
    const scroller = ref.current;
    if (!scroller) {
      return;
    }
    scroller.scrollTo({
      left: Math.max(0, scroller.scrollLeft - 250),
      behavior: "smooth"
    });
  };
  const handleScrollRight = () => {
    const scroller = ref.current;
    if (!scroller) {
      return;
    }
    scroller.scrollTo({
      left: Math.min(scroller.scrollWidth - scroller.clientWidth, scroller.scrollLeft + 250),
      behavior: "smooth"
    });
  };
  const initialInputEntries = useOpgavekompleksetEntry({
    select: entry => Object.entries(entry?.data.tasks[props.taskEntryKey]?.details ?? {}).map(([key, value]) => {
      const typedValue = sequenceTileInput.entry.asTypeSafe(value);
      if (typedValue) {
        return {
          nodeId: key,
          ...typedValue
        };
      }
    }).filter(it => !!it)
  });
  const [inputEntries, setInputEntries] = useState(initialInputEntries);
  const inputs = useMemo(() => orderInputs(props.task.content, inputEntries), [inputEntries, props.task.content]);
  const draggableInputs = inputs.filter(it => !it.options.fixed);
  const sensors = useSensors(useSensor(TouchSensor, {
    activationConstraint: {
      distance: 6
    }
  }), useSensor(MouseSensor, {
    activationConstraint: {
      distance: 6
    }
  }), useSensor(KeyboardSensor, {
    scrollBehavior: "instant"
  }));
  const handleDragStart = event => {
    setDraggedInputId(event.active.id.toString());
  };
  const handleDragOver = event => {
    updateInputsOnDrag({
      taskEntryKey: props.taskEntryKey,
      inputs,
      update,
      updateUI: setInputEntries
    }, event);
  };
  const handleDragEnd = () => {
    setDraggedInputId(undefined);
  };
  const draggedInput = draggableInputs.find(it => it.id === draggedInputId);
  const restrictToComponent = useMemo(() => makeRestrictToRef(componentRef), [componentRef]);
  return /*#__PURE__*/jsxs("div", {
    className: wrapper,
    children: [/*#__PURE__*/jsx(CSSRefTransition, {
      in: canScrollLeft && hasOverflow,
      classNames: scrollButton,
      timeout: buttonFadeDurationMs,
      mountOnEnter: true,
      unmountOnExit: true,
      children: ref => /*#__PURE__*/jsx("div", {
        ref: ref,
        className: cx(scrollButton, "left"),
        children: /*#__PURE__*/jsx(CircularButton, {
          "aria-hidden": true,
          tabIndex: -1,
          inversed: true,
          size: isMobile ? undefined : "large",
          onClick: handleScrollLeft
        })
      })
    }), /*#__PURE__*/jsx(NoSSR, {
      children: /*#__PURE__*/jsxs(DndContext, {
        sensors: sensors,
        collisionDetection: closestCenter,
        onDragStart: handleDragStart,
        onDragOver: handleDragOver,
        onDragEnd: handleDragEnd,
        onDragCancel: handleDragEnd,
        modifiers: [restrictToHorizontalAxis, restrictToComponent],
        autoScroll: false,
        children: [/*#__PURE__*/jsx(SortableContext, {
          items: draggableInputs,
          strategy: horizontalListSortingStrategy,
          children: /*#__PURE__*/jsxs("div", {
            ref: ref,
            className: container,
            "data-is-drag-active": !!draggedInputId,
            children: [inputs.map(input => /*#__PURE__*/jsxs(Fragment, {
              children: [input.options.fixed ? /*#__PURE__*/jsx(SequenceInput, {
                node: input,
                taskEntryKey: props.taskEntryKey
              }) : /*#__PURE__*/jsx(DraggableInput, {
                isDraggingItem: draggedInputId === input.id,
                node: input,
                taskEntryKey: props.taskEntryKey
              }), /*#__PURE__*/jsx(ChevronIcon, {
                className: chevron,
                height: 8,
                strokeWidth: 3
              })]
            }, input.id)), /*#__PURE__*/jsx(OpgavekompleksetTaskDragOverlay, {
              task: props.task,
              dropAnimation: {
                ...defaultDropAnimation,
                sideEffects: defaultDropAnimationSideEffects({
                  styles: {
                    active: {
                      opacity: "0.5"
                    }
                  }
                })
              },
              children: draggedInput && /*#__PURE__*/jsx(SequenceInput, {
                isDraggable: true,
                isDragOverlay: true,
                node: draggedInput,
                taskEntryKey: props.taskEntryKey
              }, draggedInput.id)
            })]
          })
        }), /*#__PURE__*/jsx(TaskAutoScroller, {
          taskRef: ref,
          horisontalScroller: ref
        })]
      })
    }), /*#__PURE__*/jsx(CSSRefTransition, {
      in: canScrollRight && hasOverflow,
      classNames: scrollButton,
      timeout: buttonFadeDurationMs,
      mountOnEnter: true,
      unmountOnExit: true,
      children: ref => /*#__PURE__*/jsx("div", {
        ref: ref,
        className: cx(scrollButton, "right"),
        children: /*#__PURE__*/jsx(CircularButton, {
          "aria-hidden": true,
          tabIndex: -1,
          size: isMobile ? undefined : "large",
          onClick: handleScrollRight
        })
      })
    })]
  });
}

export { HorisontalInputContainer };
//# sourceMappingURL=container.input.js.map
